import Alert from '@justgiving/alert';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../client/config';
import usePost from '../../../hooks/usePost';
import ImageUpload from '../../ImageUpload/ImageUpload';
import styles from '../Timeline.scss';
import { TimelineImage } from '../timelineTypes';

export interface MediaUploadSectionProps {
  className?: string;
  submitSucceeded: boolean;
  onImageSelected: (image: TimelineImage) => void;
  onImageUploaded: (image: TimelineImage) => void;
  onImageUploadFailure: (image: TimelineImage) => void;
  showImageUploadButton?: boolean;
}

interface ImageResponse {
  url: string;
}

const MediaUploadSection: FunctionComponent<MediaUploadSectionProps> = ({
  className = '',
  submitSucceeded,
  onImageSelected,
  onImageUploaded,
  onImageUploadFailure,
  showImageUploadButton = true,
}) => {
  const [imageUploadError, setImageUploadError] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<TimelineImage | null>();
  const { t } = useTranslation('common', { useSuspense: false });

  const [post, { data: uploadedFile, error }] = usePost<ImageResponse>(
    config.imageWriteServiceUrl,
  );

  const handleImageSelected = (imageFile: File) => {
    const formData = new FormData();
    formData.append('file', imageFile);

    post({
      body: formData,
      credentials: 'omit',
      headers: {},
      timeoutInMs: 30000,
    });

    const image: TimelineImage = {
      __typename: 'ImageMedia',
      preview: URL.createObjectURL(imageFile),
      url: '',
      id: uuidv4(),
      loading: true,
    };
    onImageSelected(image);
    setSelectedImage(image);
  };

  useEffect(() => {
    if (submitSucceeded) {
      setImageUploadError('');
    }
  }, [submitSucceeded]);

  useEffect(() => {
    if (uploadedFile && selectedImage) {
      onImageUploaded({
        ...selectedImage,
        loading: false,
        url: uploadedFile.url,
      });

      setSelectedImage(null);
    }
  }, [uploadedFile, selectedImage, onImageUploaded]);

  useEffect(() => {
    if (error && selectedImage) {
      onImageUploadFailure(selectedImage);
      setSelectedImage(null);
      setImageUploadError(
        t(
          'timeline.fileUploadError',
          'Could not upload image - please try again or choose a different image',
        ),
      );
    }
  }, [error, onImageUploadFailure, selectedImage, t]);

  return (
    <div className={className}>
      <div className={styles.iconContainer}>
        {showImageUploadButton && (
          <ImageUpload
            className={styles.icon}
            onImageUploaded={handleImageSelected}
            onValidated={setImageUploadError}
          />
        )}
        {/*
        /* IconButton below is commented out until video links are a supported media type
        /*
        <IconButton className={styles.icon}>
          <Svg markup={youtubeOutlinedIcon} />
        </IconButton>
        */}
      </div>
      {imageUploadError && (
        <div data-qa="image-upload-error">
          <Alert
            type="error"
            displayType="inline"
            className="jg-space-mbmd"
            onClose={() => setImageUploadError('')}
          >
            {imageUploadError}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default MediaUploadSection;
